import { useEffect, useMemo, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import { studentDueFeeReport } from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import { batchList } from "../../api/Batch";
import { busList } from "../../api/Transport";
import { GetHostelList } from "../../api/HostelService";
import { librariesList } from "../../api/Library";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";

export default function StudentFeeDueReport() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);

  const [loadingStates, setLoadingStates] = useState({
    report: false,
    batches: false,
    hostels: false,
    buses: false,
    libraries: false,
    download: false,
  });

  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [buses, setBuses] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [filters, setFilters] = useState({});
  const [students, setStudents] = useState([]);

  /*
   * ============================================================
   * Loading Helpers
   * ============================================================
   */

  const setSectionLoading = (key, value) => {
    setLoadingStates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const isAnyLoading = useMemo(() => {
    return Object.values(loadingStates).some(Boolean);
  }, [loadingStates]);

  useEffect(() => {
    setLoading(isAnyLoading);
  }, [isAnyLoading]);

  /*
   * ============================================================
   * Sessions
   * ============================================================
   */

  useEffect(() => {
    try {
      const parseSessions = JSON.parse(
        localStorage.getItem("sessions") ?? "[]",
      );

      setSessions(Array.isArray(parseSessions) ? parseSessions : []);
    } catch (error) {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;

    setFilters((prev) => {
      if (prev.session_id || !activeSession) {
        return prev;
      }

      return {
        ...prev,
        session_id: activeSession,
      };
    });
  }, [sessions]);

  /*
   * ============================================================
   * Helpers
   * ============================================================
   */

  const inr = (n) =>
    "₹ " +
    Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  const num = (v) => Number(v || 0);

  /*
   * ============================================================
   * Student Fee Calculation
   * ============================================================
   */

  const calculateStudentTotals = (student) => {
    const batch = student?.student_batch_n_fees || [];
    const hostel = student?.student_hostel_n_fees || [];
    const transport = student?.student_transport_n_fees || [];
    const library = student?.student_library_n_fees || [];

    const batchTotals = batch.reduce(
      (acc, r) => ({
        total: acc.total + num(r.total_fee),
        discount: acc.discount + num(r.total_discount),
        final: acc.final + num(r.final_fee),
        paid: acc.paid + num(r.total_paid),
        due: acc.due + num(r.total_due),
      }),
      {
        total: 0,
        discount: 0,
        final: 0,
        paid: 0,
        due: 0,
      },
    );

    const hostelTotals = hostel.reduce(
      (acc, r) => ({
        total: acc.total + num(r.total_amount),
        discount: acc.discount,
        final: acc.final + num(r.final_amount),
        paid: acc.paid + num(r.paid_amount),
        due: acc.due + num(r.left_amount),
      }),
      {
        total: 0,
        discount: 0,
        final: 0,
        paid: 0,
        due: 0,
      },
    );

    const transportTotals = transport.reduce(
      (acc, r) => {
        const final = num(r.final_fee);
        const paid = num(r.paid_fee);

        return {
          total: acc.total + num(r.total_fee),
          discount: acc.discount + num(r.discount),
          final: acc.final + final,
          paid: acc.paid + paid,
          due: acc.due + (final - paid),
        };
      },
      {
        total: 0,
        discount: 0,
        final: 0,
        paid: 0,
        due: 0,
      },
    );

    const libraryTotals = library.reduce(
      (acc, r) => ({
        total: acc.total + num(r.total_amount),
        discount: acc.discount,
        final: acc.final + num(r.final_amount),
        paid: acc.paid + num(r.paid_amount),
        due: acc.due + num(r.left_amount),
      }),
      {
        total: 0,
        discount: 0,
        final: 0,
        paid: 0,
        due: 0,
      },
    );

    const totalFee =
      batchTotals.total +
      hostelTotals.total +
      transportTotals.total +
      libraryTotals.total;

    const totalDiscount =
      batchTotals.discount +
      hostelTotals.discount +
      transportTotals.discount +
      libraryTotals.discount;

    const finalFee =
      batchTotals.final +
      hostelTotals.final +
      transportTotals.final +
      libraryTotals.final;

    const totalPaid =
      batchTotals.paid +
      hostelTotals.paid +
      transportTotals.paid +
      libraryTotals.paid;

    const totalDue =
      batchTotals.due +
      hostelTotals.due +
      transportTotals.due +
      libraryTotals.due;

    const breakdown = [
      {
        type: "Batch Fee",
        amount: batchTotals.due,
        count: batch.length,
      },
      {
        type: "Hostel Fee",
        amount: hostelTotals.due,
        count: hostel.length,
      },
      {
        type: "Transport Fee",
        amount: transportTotals.due,
        count: transport.length,
      },
      {
        type: "Library Fee",
        amount: libraryTotals.due,
        count: library.length,
      },
    ].filter((b) => b.count > 0);

    return {
      totalFee,
      totalDiscount,
      finalFee,
      totalPaid,
      totalDue,
      breakdown,
    };
  };

  /*
   * ============================================================
   * Student Due Fee Report
   * ============================================================
   */

  const getStudentDueFeeReport = async () => {
    setSectionLoading("report", true);

    try {
      let addUrl = "";

      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        if (value !== undefined && value !== null && value !== "") {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await studentDueFeeReport(addUrl);

      if (response?.data?.status) {
        setStudents(response?.data?.data || []);
      } else {
        setStudents([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No records found.",
        );
      }
    } catch (error) {
      setStudents([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setSectionLoading("report", false);
    }
  };

  /*
   * ============================================================
   * Batch List
   * ============================================================
   */

  const getBatchList = async () => {
    setSectionLoading("batches", true);

    try {
      const response = await batchList(
        filters?.session_id ? `session_id=${filters?.session_id}` : "",
      );

      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        setBatches([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Batch data not found.",
        );
      }
    } catch (error) {
      setBatches([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to load batches.",
      );
    } finally {
      setSectionLoading("batches", false);
    }
  };

  /*
   * ============================================================
   * Hostel List
   * ============================================================
   */

  const getHostelList = async () => {
    setSectionLoading("hostels", true);

    try {
      const response = await GetHostelList("limit=all");

      if (response?.data?.status) {
        setHostels(response?.data?.data || []);
      } else {
        setHostels([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Hostel data not found.",
        );
      }
    } catch (error) {
      setHostels([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to load hostels.",
      );
    } finally {
      setSectionLoading("hostels", false);
    }
  };

  /*
   * ============================================================
   * Bus List
   * ============================================================
   */

  const getBusList = async () => {
    setSectionLoading("buses", true);

    try {
      const response = await busList();

      if (response?.data?.status) {
        setBuses(response?.data?.data || []);
      } else {
        setBuses([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Bus data not found.",
        );
      }
    } catch (error) {
      setBuses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to load buses.",
      );
    } finally {
      setSectionLoading("buses", false);
    }
  };

  /*
   * ============================================================
   * Library List
   * ============================================================
   */

  const getLibrariesList = async () => {
    setSectionLoading("libraries", true);

    try {
      const response = await librariesList();

      if (response?.data?.status) {
        setLibraries(response?.data?.data || []);
      } else {
        setLibraries([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Library data not found.",
        );
      }
    } catch (error) {
      setLibraries([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to load libraries.",
      );
    } finally {
      setSectionLoading("libraries", false);
    }
  };

  /*
   * ============================================================
   * Filter Effects
   * ============================================================
   */

  useEffect(() => {
    if (!sessions.length || !filters?.session_id) {
      return;
    }

    getStudentDueFeeReport();
  }, [
    filters?.session_id,
    filters?.search,
    filters?.rte_status,
    filters?.batch_id,
    filters?.hostel_id,
    filters?.bus_id,
    filters?.library_id,
  ]);

  useEffect(() => {
    if (filters?.session_id) {
      getBatchList();
    }
  }, [filters?.session_id]);

  useEffect(() => {
    getHostelList();
    getBusList();
    getLibrariesList();
  }, []);

  /*
   * ============================================================
   * Rows
   * ============================================================
   */

  const rows = useMemo(() => {
    return students
      .map((s) => ({
        student: s,
        totals: calculateStudentTotals(s),
      }))
      ?.filter((r) => r.totals.totalDue > 0);
  }, [students]);

  /*
   * ============================================================
   * Summary
   * ============================================================
   */

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        students: acc.students + 1,
        totalFee: acc.totalFee + r.totals.totalFee,
        totalDiscount: acc.totalDiscount + r.totals.totalDiscount,
        finalFee: acc.finalFee + r.totals.finalFee,
        totalPaid: acc.totalPaid + r.totals.totalPaid,
        totalDue: acc.totalDue + r.totals.totalDue,
      }),
      {
        students: 0,
        totalFee: 0,
        totalDiscount: 0,
        finalFee: 0,
        totalPaid: 0,
        totalDue: 0,
      },
    );
  }, [rows]);

  /*
   * ============================================================
   * Module Badge Colors
   * ============================================================
   */

  const MODULE_BADGE_COLOR = {
    "Batch Fee": "primary",
    "Hostel Fee": "success",
    "Transport Fee": "warning",
    "Library Fee": "info",
  };

  /*
   * ============================================================
   * Breakdown
   * ============================================================
   */

  const renderBreakdown = (breakdown) => {
    if (!breakdown?.length) {
      return "-";
    }

    return breakdown.map((b, i) => {
      const color = MODULE_BADGE_COLOR[b.type] || "secondary";

      return (
        <span
          key={i}
          className={`badge bg-${color}-subtle text-${color} rounded-pill me-1 mb-1 fw-semibold text-nowrap`}
          style={{
            fontSize: "0.7rem",
          }}
        >
          {b.type} · {inr(b.amount)}
        </span>
      );
    });
  };

  /*
   * ============================================================
   * Filter Update
   * ============================================================
   */

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /*
   * ============================================================
   * Clear Filters
   * ============================================================
   */

  const clearFilters = () => {
    const activeSession = sessions?.find((s) => Number(s.status) === 1)?.id;

    setFilters(
      activeSession
        ? {
            session_id: activeSession,
          }
        : {},
    );
  };

  /*
   * ============================================================
   * Download / Print
   * ============================================================
   */

  const handleDownload = async (type = "excel") => {
    setSectionLoading("download", true);

    try {
      const downloadData = rows.map(({ student, totals }) => ({
        "Registration No": student.registration_no,

        "Student Name": student.name,

        "Father Name": student.father_name,

        Phone: student.phone || "",
        Batch: student?.student_batch_info?.batch?.title || "",

        "Fee Modules": totals?.breakdown
          ?.map((m) => `${m.type} (${inr(m.amount)})`)
          .join(", "),

        "Payable Fee": totals.finalFee,

        Received: totals.totalPaid,

        Due: totals.totalDue,
      }));

      if (!downloadData.length) {
        SweetAlertShow("info", "Info", "No records found");

        return;
      }
      const totalRow = {
        "Registration No": "",
        "Student Name": "TOTAL",
        "Father Name": "",
        Phone: "",
        "Fee Modules": `${summary.students} Students`,
        "Payable Fee": summary.finalFee,
        Received: summary.totalPaid,
        Due: summary.totalDue,
      };
      downloadData.push(totalRow);

      if (type === "excel") {
        const now = new Date();

        const fileName =
          `Report_${now.getFullYear()}_` +
          `${String(now.getMonth() + 1).padStart(2, "0")}_` +
          `${String(now.getDate()).padStart(2, "0")}_` +
          `${String(now.getHours()).padStart(2, "0")}_` +
          `${String(now.getMinutes()).padStart(2, "0")}`;

        await Promise.resolve(DownloadExcel(downloadData, [], fileName, []));
      } else {
        await Promise.resolve(PrintPage(downloadData));
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.message ??
          "Unable to generate report.",
      );
    } finally {
      setSectionLoading("download", false);
    }
  };

  /*
   * ============================================================
   * Skeleton Components
   * ============================================================
   */

  const FilterSkeleton = ({ width = "100%" }) => (
    <div className="placeholder-glow" style={{ width }}>
      <span
        className="placeholder rounded-3 d-block"
        style={{
          height: "55px",
          width: "100%",
        }}
      ></span>
    </div>
  );

  const SummarySkeleton = () => (
    <div className="card border-0 shadow-sm rounded-4 h-100">
      <div className="card-body p-3">
        <div className="placeholder-glow">
          <span
            className="placeholder rounded-3 mb-3"
            style={{
              width: "42px",
              height: "42px",
            }}
          ></span>

          <span
            className="placeholder col-7 d-block mb-2"
            style={{
              height: "22px",
            }}
          ></span>

          <span
            className="placeholder col-5 d-block"
            style={{
              height: "12px",
            }}
          ></span>
        </div>
      </div>
    </div>
  );

  const TableRowSkeleton = ({ index }) => (
    <tr key={`skeleton-${index}`}>
      <td className="ps-4">
        <span className="placeholder-glow">
          <span className="placeholder col-5"></span>
        </span>
      </td>

      <td>
        <div className="placeholder-glow">
          <span className="placeholder col-8 d-block mb-2"></span>
          <span className="placeholder col-5 d-block"></span>
        </div>
      </td>

      <td>
        <span className="placeholder-glow">
          <span className="placeholder col-8"></span>
        </span>
      </td>

      <td>
        <span className="placeholder-glow">
          <span className="placeholder col-8"></span>
        </span>
      </td>

      <td>
        <div className="placeholder-glow">
          <span
            className="placeholder rounded-pill me-1"
            style={{
              width: "80px",
              height: "22px",
            }}
          ></span>
          <span
            className="placeholder rounded-pill"
            style={{
              width: "70px",
              height: "22px",
            }}
          ></span>
        </div>
      </td>

      <td className="text-end">
        <span className="placeholder-glow">
          <span className="placeholder col-7"></span>
        </span>
      </td>

      <td className="text-end">
        <span className="placeholder-glow">
          <span className="placeholder col-7"></span>
        </span>
      </td>

      <td className="text-end">
        <span className="placeholder-glow">
          <span className="placeholder col-7"></span>
        </span>
      </td>

      <td className="pe-4">
        <div className="d-flex gap-2 justify-content-end">
          <span
            className="placeholder rounded-2"
            style={{
              width: "34px",
              height: "32px",
            }}
          ></span>

          <span
            className="placeholder rounded-2"
            style={{
              width: "34px",
              height: "32px",
            }}
          ></span>
        </div>
      </td>
    </tr>
  );

  /*
   * ============================================================
   * Render
   * ============================================================
   */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* =====================================================
                        HEADER
                    ====================================================== */}

                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 48,
                              height: 48,
                              background:
                                "linear-gradient(135deg, #ef4444, #f97316)",
                            }}
                          >
                            <i className="ri-hand-coin-line fs-4"></i>
                          </div>

                          <div>
                            <h5 className="m-0 fw-bold">
                              Student Due Fee Report
                            </h5>

                            <small className="text-muted">
                              Complete student outstanding fee summary
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-danger-subtle text-danger rounded-pill px-3 py-2">
                            <i className="ri-error-warning-line me-1"></i>
                            Due: {inr(summary.totalDue)}
                          </span>

                          <button
                            type="button"
                            className="btn btn-light border rounded-3"
                            onClick={clearFilters}
                            disabled={loading}
                            title="Clear Filters"
                          >
                            <i className="ri-filter-off-line me-1"></i>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        FILTERS
                    ====================================================== */}

                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                          style={{
                            width: 38,
                            height: 38,
                          }}
                        >
                          <i className="ri-filter-3-line"></i>
                        </div>

                        <div>
                          <h6 className="m-0 fw-bold">Report Filters</h6>

                          <small className="text-muted">
                            Filter students by academic and facility details
                          </small>
                        </div>
                      </div>

                      <div className="row g-3">
                        {/* Search */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.report ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline position-relative">
                              <input
                                type="text"
                                className="form-control ps-5"
                                placeholder="Name / Reg. No / Phone"
                                value={filters?.search || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    search: e.target.value,
                                  })
                                }
                              />

                              <i
                                className="ri-search-line position-absolute text-muted"
                                style={{
                                  left: "18px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  zIndex: 5,
                                }}
                              ></i>

                              <label>Search Student</label>
                            </div>
                          )}
                        </div>

                        {/* Session */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.report ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.session_id || ""}
                                onChange={(e) =>
                                  updateFilter("session_id", e.target.value)
                                }
                              >
                                <option value="">Select Session</option>

                                {sessions?.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.session} :{" "}
                                    {Number(s.status) === 1
                                      ? "Active"
                                      : "Inactive"}
                                  </option>
                                ))}
                              </select>

                              <label>
                                <i className="ri-calendar-line me-1"></i>
                                Session
                              </label>
                            </div>
                          )}
                        </div>

                        {/* RTE */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.report ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.rte_status || ""}
                                onChange={(e) =>
                                  updateFilter("rte_status", e.target.value)
                                }
                              >
                                <option value="">All</option>

                                <option value="RTE">RTE</option>

                                <option value="NON-RTE">NON-RTE</option>
                              </select>

                              <label>
                                <i className="ri-user-star-line me-1"></i>
                                RTE Status
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Batch */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.batches ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.batch_id || ""}
                                onChange={(e) =>
                                  updateFilter("batch_id", e.target.value)
                                }
                              >
                                <option value="">All</option>

                                <option value="only">Only Batch</option>

                                {batches?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.title} ({item?.course?.title})
                                  </option>
                                ))}
                              </select>

                              <label>
                                <i className="ri-group-line me-1"></i>
                                Batch
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Hostel */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.hostels ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.hostel_id || ""}
                                onChange={(e) =>
                                  updateFilter("hostel_id", e.target.value)
                                }
                              >
                                <option value="">All</option>

                                <option value="only">Only Hostel</option>

                                {hostels?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.name}
                                  </option>
                                ))}
                              </select>

                              <label>
                                <i className="ri-hotel-line me-1"></i>
                                Hostel
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Bus */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.buses ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.bus_id || ""}
                                onChange={(e) =>
                                  updateFilter("bus_id", e.target.value)
                                }
                              >
                                <option value="">All</option>

                                <option value="only">Only Bus</option>

                                {buses?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.bus_number}
                                  </option>
                                ))}
                              </select>

                              <label>
                                <i className="ri-bus-line me-1"></i>
                                Transport
                              </label>
                            </div>
                          )}
                        </div>

                        {/* Library */}

                        <div className="col-xl-2 col-lg-3 col-md-4">
                          {loadingStates.libraries ? (
                            <FilterSkeleton />
                          ) : (
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select rounded-3"
                                value={filters?.library_id || ""}
                                onChange={(e) =>
                                  updateFilter("library_id", e.target.value)
                                }
                              >
                                <option value="">All</option>

                                <option value="only">Only Library</option>

                                {libraries?.map((item) => (
                                  <option key={item?.id} value={item?.id}>
                                    {item?.name}
                                  </option>
                                ))}
                              </select>

                              <label>
                                <i className="ri-book-open-line me-1"></i>
                                Library
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        SUMMARY CARDS
                    ====================================================== */}

                    <div className="row g-3 mb-3">
                      {[
                        {
                          label: "Students",
                          value: summary.students,
                          icon: "ri-group-line",
                          accent: "#4f46e5",
                          isCurrency: false,
                        },
                        {
                          label: "Total Fee",
                          value: summary.totalFee,
                          icon: "ri-file-list-3-line",
                          accent: "#6366f1",
                          isCurrency: true,
                        },
                        {
                          label: "Discount",
                          value: summary.totalDiscount,
                          icon: "ri-price-tag-3-line",
                          accent: "#f59e0b",
                          isCurrency: true,
                        },
                        {
                          label: "Payable Fee",
                          value: summary.finalFee,
                          icon: "ri-bill-line",
                          accent: "#0ea5e9",
                          isCurrency: true,
                        },
                        {
                          label: "Received",
                          value: summary.totalPaid,
                          icon: "ri-wallet-3-line",
                          accent: "#22c55e",
                          isCurrency: true,
                        },
                        {
                          label: "Due",
                          value: summary.totalDue,
                          icon: "ri-error-warning-line",
                          accent: "#ef4444",
                          isCurrency: true,
                        },
                      ].map((k) => (
                        <div
                          className="col-xl-2 col-lg-4 col-md-6 col-12"
                          key={k.label}
                        >
                          {loadingStates.report ? (
                            <SummarySkeleton />
                          ) : (
                            <div
                              className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden"
                              style={{
                                borderTop: `3px solid ${k.accent}`,
                              }}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <small className="text-muted text-uppercase fw-semibold">
                                      {k.label}
                                    </small>

                                    <h5
                                      className="m-0 mt-1 fw-bold"
                                      style={{
                                        color: k.accent,
                                      }}
                                    >
                                      {k.isCurrency ? inr(k.value) : k.value}
                                    </h5>
                                  </div>

                                  <div
                                    className="rounded-3 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: 42,
                                      height: 42,
                                      background: `${k.accent}1a`,
                                      color: k.accent,
                                    }}
                                  >
                                    <i className={`${k.icon} fs-5`}></i>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* =====================================================
                        REPORT TABLE
                    ====================================================== */}

                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      {/* Table Header */}

                      <div className="px-4 py-3 border-bottom">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center text-white"
                              style={{
                                width: 44,
                                height: 44,
                                background:
                                  "linear-gradient(135deg, #ef4444, #f59e0b)",
                              }}
                            >
                              <i className="ri-file-chart-line fs-5"></i>
                            </div>

                            <div>
                              <h6 className="m-0 fw-bold">
                                Students With Due Fee
                              </h6>

                              <small className="text-muted">
                                Aggregated across batch, hostel, transport &amp;
                                library fees
                              </small>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold px-3 py-2 rounded-pill">
                              <i className="ri-user-line me-1"></i>
                              {loadingStates.report
                                ? "Loading..."
                                : `${rows.length} student${
                                    rows.length === 1 ? "" : "s"
                                  }`}
                            </span>

                            <button
                              type="button"
                              className="btn btn-outline-primary rounded-3"
                              onClick={() => handleDownload("excel")}
                              disabled={
                                loadingStates.download || loadingStates.report
                              }
                            >
                              {loadingStates.download ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Preparing...
                                </>
                              ) : (
                                <>
                                  <i className="ri-file-excel-2-line me-2"></i>
                                  Download
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-info rounded-3"
                              onClick={() => handleDownload("print")}
                              disabled={
                                loadingStates.download || loadingStates.report
                              }
                            >
                              {loadingStates.download ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Preparing...
                                </>
                              ) : (
                                <>
                                  <i className="ri-printer-line me-2"></i>
                                  Print
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Table */}

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted bg-light">
                              <th className="ps-4 py-3 fw-semibold">#</th>

                              <th className="py-3 fw-semibold">Student</th>

                              <th className="py-3 fw-semibold">Phone</th>

                              <th className="py-3 fw-semibold">Batch</th>

                              <th className="py-3 fw-semibold">Fee Modules</th>

                              <th className="py-3 fw-semibold text-end">
                                Payable Fee
                              </th>

                              <th className="py-3 fw-semibold text-end">
                                Received
                              </th>

                              <th className="py-3 fw-semibold text-end">Due</th>

                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {/* ===============================
                                REPORT LOADING
                            ================================ */}

                            {loadingStates.report ? (
                              Array.from({
                                length: 7,
                              }).map((_, index) => (
                                <TableRowSkeleton key={index} index={index} />
                              ))
                            ) : rows.length > 0 ? (
                              rows.map(({ student, totals }, index) => (
                                <tr
                                  key={
                                    student.id ??
                                    student.registration_no ??
                                    index
                                  }
                                >
                                  <td className="ps-4">
                                    <span
                                      className="badge bg-light text-dark border rounded-circle d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        width: 30,
                                        height: 30,
                                      }}
                                    >
                                      {index + 1}
                                    </span>
                                  </td>

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-bold"
                                        style={{
                                          width: 38,
                                          height: 38,
                                          minWidth: 38,
                                        }}
                                      >
                                        {(student?.name || "S")
                                          .charAt(0)
                                          .toUpperCase()}
                                      </div>

                                      <div>
                                        <Link
                                          className="fw-semibold text-decoration-none"
                                          to={`/student/details?id=${student.id}`}
                                        >
                                          {student.name}
                                        </Link>

                                        <div className="text-muted small">
                                          <i className="ri-id-card-line me-1"></i>
                                          {student.registration_no || "-"}
                                        </div>

                                        <div className="text-muted small">
                                          <i className="ri-user-line me-1"></i>
                                          {student.father_name || "-"}
                                        </div>
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    {student.phone ? (
                                      <span className="text-nowrap">
                                        <i className="ri-phone-line text-success me-1"></i>
                                        {student.phone}
                                      </span>
                                    ) : (
                                      "-"
                                    )}
                                  </td>

                                  <td>
                                    {student?.student_batch_info?.batch
                                      ?.title ? (
                                      <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
                                        <i className="ri-group-line me-1"></i>
                                        {
                                          student?.student_batch_info?.batch
                                            ?.title
                                        }
                                      </span>
                                    ) : (
                                      "-"
                                    )}
                                  </td>

                                  <td style={{ minWidth: 210 }}>
                                    {totals.breakdown.length > 0 ? (
                                      <div className="d-flex flex-wrap">
                                        {renderBreakdown(totals.breakdown)}
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>

                                  <td className="text-end text-nowrap">
                                    <span className="fw-semibold">
                                      {inr(totals.finalFee)}
                                    </span>
                                  </td>

                                  <td className="text-end text-nowrap">
                                    <span className="badge bg-success-subtle text-success rounded-pill px-3 py-2 fw-semibold">
                                      <i className="ri-check-double-line me-1"></i>
                                      {inr(totals.totalPaid)}
                                    </span>
                                  </td>

                                  <td className="text-end text-nowrap">
                                    <span className="badge bg-danger-subtle text-danger rounded-pill px-3 py-2 fw-bold">
                                      <i className="ri-error-warning-line me-1"></i>
                                      {inr(totals.totalDue)}
                                    </span>
                                  </td>

                                  <td className="pe-4">
                                    <div className="d-flex gap-2 justify-content-end">
                                      {hasAccess("/student/details") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm rounded-3"
                                          to={`/student/details?id=${student.id}`}
                                          title="View Student"
                                        >
                                          <i className="ri-eye-line ri-20px"></i>
                                        </Link>
                                      )}

                                      {hasAccess("/student/fees/deposit") && (
                                        <Link
                                          className="btn btn-label-danger btn-sm rounded-3"
                                          to={`/student/fees/deposit?student_id=${student.id}`}
                                          title="Pay Fee"
                                        >
                                          <i className="ri-hand-coin-line ri-20px"></i>
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              /* ===============================
                                  EMPTY STATE
                              ================================ */

                              <tr>
                                <td colSpan="9" className="text-center py-5">
                                  <div
                                    className="d-flex align-items-center justify-content-center mx-auto mb-3 rounded-circle bg-light"
                                    style={{
                                      width: 72,
                                      height: 72,
                                    }}
                                  >
                                    <i className="ri-inbox-archive-line fs-1 text-muted"></i>
                                  </div>

                                  <h6 className="fw-bold mb-1">
                                    No Due Fee Records Found
                                  </h6>

                                  <p className="text-muted mb-0">
                                    No students with outstanding fees match the
                                    selected filters.
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>

                          {/* ===============================
                              FOOTER TOTAL
                          ================================ */}

                          {!loadingStates.report && rows.length > 0 && (
                            <tfoot>
                              <tr
                                style={{
                                  background: "#f8f9fc",
                                }}
                              >
                                <td
                                  colSpan="5"
                                  className="ps-4 py-3 text-end fw-bold"
                                >
                                  <i className="ri-calculator-line me-1"></i>
                                  Total
                                </td>

                                <td className="py-3 fw-bold text-end text-nowrap">
                                  {inr(summary.finalFee)}
                                </td>

                                <td className="py-3 fw-bold text-end text-nowrap text-success">
                                  {inr(summary.totalPaid)}
                                </td>

                                <td className="py-3 fw-bold text-end text-nowrap text-danger">
                                  {inr(summary.totalDue)}
                                </td>

                                <td className="pe-4"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
